"use client";

import { lazy, Suspense, useEffect, useState } from "react";
import { HiChevronDown } from "react-icons/hi2";
import Navbar from "../Navbar";
import { content } from "../../data/siteData";

const Spline = lazy(() => import("@splinetool/react-spline"));

const SplineLoader = () => (
  <div className="w-full h-full flex items-center justify-center">
    <div className="w-[200px] md:w-[300px] lg:w-[400px] h-[200px] md:h-[300px] lg:h-[400px] bg-gray-200 rounded-full animate-pulse" />
  </div>
);

// The Spline runtime is ~2 MB of JS (three.js + physics). Below md the scene is hidden,
// so never load it there; on larger screens load it once the browser is idle so it
// doesn't delay the first paint.
const useDeferredSpline = () => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 768px)");
    let idleId;
    const schedule = () => {
      if (!media.matches || idleId !== undefined) return;
      idleId = window.requestIdleCallback
        ? window.requestIdleCallback(() => setReady(true), { timeout: 4000 })
        : window.setTimeout(() => setReady(true), 2000);
    };
    schedule();
    media.addEventListener("change", schedule);
    return () => {
      media.removeEventListener("change", schedule);
      if (idleId === undefined) return;
      if (window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      else window.clearTimeout(idleId);
    };
  }, []);

  return ready;
};

const HeroSection = ({ activeSection, locale }) => {
  const t = content[locale].hero;
  const showSpline = useDeferredSpline();

  return (
    <div className="min-h-screen flex flex-col justify-between bg-white relative">
      <header>
        <Navbar activeSection={activeSection} locale={locale} />
      </header>

      <section
        aria-label="Introduction"
        className="flex-1 flex flex-col lg:flex-row items-center justify-center px-6 md:px-12 max-w-7xl mx-auto w-full gap-8"
      >
        <div className="flex flex-col gap-4 text-center lg:text-left md:flex-1 justify-center">
          <h1 className="hero-text font-montserrat font-bold text-4xl sm:text-6xl md:text-7xl lg:text-[96px] leading-none text-[#333333]">
            Alexandre Fernandes
          </h1>

          <p className="hero-text font-poppins font-extralight text-xl sm:text-2xl md:text-3xl lg:text-[40px] leading-relaxed text-[#333333]">
            {t.role}
          </p>

          <p className="hero-text font-poppins font-light text-base md:text-lg lg:text-xl text-[#555555] max-w-xl">
            {t.intro}
          </p>

          <div className="hero-text flex flex-col sm:flex-row gap-4 mt-4 justify-center lg:justify-start">
            <a
              href="#projects"
              className="px-8 py-3 bg-[#333333] text-white font-poppins font-normal text-base md:text-lg rounded-lg hover:bg-[#555555] transition-colors text-center focus:outline-none focus:ring-2 focus:ring-[#333333] focus:ring-offset-2"
            >
              {t.viewProjects}
            </a>
            <a
              href="#contact"
              className="px-8 py-3 border border-[#333333] text-[#333333] font-poppins font-normal text-base md:text-lg rounded-lg hover:bg-[#333333] hover:text-white transition-colors text-center focus:outline-none focus:ring-2 focus:ring-[#333333] focus:ring-offset-2"
            >
              {t.getInTouch}
            </a>
          </div>
        </div>

        <div className="hidden md:flex flex-1 h-[300px] md:h-[400px] lg:h-[500px] w-full items-center justify-center relative">
          <div className="absolute w-[200px] md:w-[300px] lg:w-[400px] h-[200px] md:h-[300px] lg:h-[400px] bg-gray-600/40 rounded-full blur-[80px] -z-10"></div>
          {showSpline ? (
            <Suspense fallback={<SplineLoader />}>
              {/* Scene and Draco decoder are self-hosted in public/spline so visitors don't
                  contact Spline or Google. After editing the scene in Spline, download the new
                  scene.splinecode (Export > Code > self-host) into public/spline. */}
              <Spline scene="/spline/scene.splinecode" wasmPath="/spline" />
            </Suspense>
          ) : (
            <SplineLoader />
          )}
        </div>
      </section>

      <div
        className="absolute bottom-8 md:bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce"
        aria-label={t.scrollLabel}
      >
        <p className="font-poppins font-light text-xs md:text-sm text-[#333333]">
          {t.scroll}
        </p>
        <HiChevronDown
          className="text-xl md:text-2xl text-[#333333]"
          aria-hidden="true"
        />
      </div>
    </div>
  );
};

export default HeroSection;
